import React, { useEffect, useState, useContext } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import Header from "../components/header";
import Menu from "../components/menu";
import MainRoute from "../router/mainRoute";
import SideBar from "../components/sideBar";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { mainLayout } from "../global/styles";
import * as apiService from "../../api";
import SessionTimeout from "../components/sessionTimeout";
import DialogSessionTimeout from "../components/dialogSessionTimeout";
import { ConfigurationContext } from "../context/configurationContext";
import { setFeatureFlags, setIsFeatureFlagLoad } from "../redux/features/configuration/configurationSlice";
import { setIsUltra } from "../redux/features/afiliado/afiliadoSlice";
import OnboardingContainer from "../components/onboarding/onboardingContainer";
import colors from "../../config/colors";

const drawerWidth = 250;

// Componente interno que tiene acceso a useLocation
const LayoutContent = () => {
  const { isLoginPage, getAlertaPersonalizada } = useContext(ConfigurationContext);
  const [sessionOpenModal, setSessionOpenModal] = useState(false);
  const handlerCloseSessionModal = () => setSessionOpenModal(false);
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const featureFlags = useSelector((state) => state.configuration.feauteFlags);
  const location = useLocation();

  const dispatch = useDispatch();

  // Detectar si estamos en onboarding
  const isOnboardingPage = location.pathname === "/onboarding";

  // Ocultar layout si es login o onboarding
  const hideLayout = isLoginPage || isOnboardingPage;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Detección Ultra a nivel de app — se ejecuta en cuanto hay codigoAfiliado
  // Esto garantiza que el tema Ultra aplica en TODAS las páginas, no solo en Home,
  // y que la transición ocurre lo antes posible (antes de que Home.jsx monte).
  useEffect(() => {
    if (!infoAfiliado.codigoAfiliado) return;
    // Si ya tenemos el valor desde sessionStorage, no rellamamos la API
    if (sessionStorage.getItem('_isUltra') === '1') {
      dispatch(setIsUltra(true));
      return;
    }
    const detectarUltra = async () => {
      try {
        let planes = [];
        try {
          const res = await apiService.afiliado.getPlanAfiliado(infoAfiliado.codigoAfiliado);
          const data = res.data?.result ?? res.data;
          if (data && res.data?.resultType !== 2) {
            planes = Array.isArray(data) ? data : [data];
          }
        } catch { /* ignorar */ }

        if (planes.length === 0) {
          const res = await apiService.afiliado.getAfiliado(infoAfiliado.codigoAfiliado);
          const datosAfiliacion = res.data?.result?.datosAfiliacion ?? [];
          planes = Array.isArray(datosAfiliacion) ? datosAfiliacion : [];
        }

        const ultraDetectado = planes.some((p) => {
          if (p.esVip === true || p.isVip === true || p.vip === true) return true;
          const nombre = (p.planes_Nombre ?? p.nombre ?? p.nombre_plan ?? p.plan ?? p.descripcion ?? '').toLowerCase();
          return nombre.includes('ultra');
        });
        dispatch(setIsUltra(ultraDetectado));
      } catch (err) {
        console.error('[Layout] Error detectando Ultra:', err);
      }
    };
    detectarUltra();
  }, [infoAfiliado.codigoAfiliado]);

  useEffect(() => {
    apiService.utility
      .getFeatureFlags()
      .then((response) => {
        dispatch(setFeatureFlags(response.data.result));
        dispatch(setIsFeatureFlagLoad(true));
      })
      .catch(console.log);

    return () => { };
  }, [isLoginPage, dispatch]);

  useEffect(() => {
    getAlertaPersonalizada()
  }, [])

  const isDark = infoAfiliado.isUltra && infoAfiliado.ultraDarkMode;
  const isUltraLight = infoAfiliado.isUltra && !infoAfiliado.ultraDarkMode;

  // Sync body class for scrollbar + global theming
  useEffect(() => {
    if (isDark) {
      document.body.classList.add('ultra-theme');
      document.body.classList.remove('ultra-light-theme');
    } else if (isUltraLight) {
      document.body.classList.add('ultra-light-theme');
      document.body.classList.remove('ultra-theme');
    } else {
      document.body.classList.remove('ultra-theme', 'ultra-light-theme');
    }
  }, [isDark, isUltraLight]);

  return (
    <OnboardingContainer>
      <Box sx={{
        display: "flex",
        minHeight: '100vh',
        backgroundColor: isDark ? colors.ultraDark : isUltraLight ? colors.ulBg : undefined,
        color: isDark ? colors.ultraSilver : isUltraLight ? colors.ulText : undefined,
        transition: 'background-color 0.45s ease, color 0.45s ease',
      }}>
        {!hideLayout && (
          <Box>
            <Menu
              handleDrawerToggle={handleDrawerToggle}
              mobileOpen={mobileOpen}
              drawerWidth={drawerWidth}
              regimen={infoAfiliado.regimen}
            />
          </Box>
        )}
        <Box
          component="main"
          sx={!hideLayout ? {
            ...mainLayout,
            width: mainLayout.width,
            backgroundColor: isDark
              ? `${colors.ultraDark} !important`
              : isUltraLight
                ? `${colors.ulBg} !important`
                : undefined,
            backgroundImage: isDark
              ? `linear-gradient(180deg, ${colors.ultraDark} 0%, #0e1310 100%)`
              : undefined,
            transition: 'background-color 0.45s ease, background-image 0.45s ease',
          } : null}
        >
          {!hideLayout ? (
            <Header handleDrawerToggle={handleDrawerToggle} />
          ) : null}

          <Box
            sx={{
              paddingRight: { xs: 2, md: 1 },
              paddingLeft: { xs: 1, md: 0 },
            }}
          >
            {MainRoute}
          </Box>
        </Box>

        {!hideLayout ? featureFlags.verRedesSociales && <SideBar /> : null}
      </Box>
      <SessionTimeout isOpen={sessionOpenModal} setOpen={setSessionOpenModal} />
      <DialogSessionTimeout
        open={sessionOpenModal}
        handlerCloseModal={handlerCloseSessionModal}
      />
    </OnboardingContainer>
  );
};

const Layout = () => {
  return (
    <Router>
      <LayoutContent />
    </Router>
  );
};

export default Layout;
